import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, Check, ChevronDown, ChevronUp, Loader2, Send, Sparkles, Square, Trash2 } from 'lucide-react'

import { AgentMarkdown } from '@klear/ui/components/agent-markdown'
import { Button } from '@klear/ui/components/button'
import { ScrollArea } from '@klear/ui/components/scroll-area'
import { Textarea } from '@klear/ui/components/textarea'
import { useAgentChat, type AgentToolRun, type AgentTurn } from '@klear/ui/hooks/use-agent-chat'
import { cn } from '@klear/ui/lib/utils'

function toolLabel(name: string): string {
  return name.replace(/_/g, ' ')
}

function ToolChip({ tool }: { tool: AgentToolRun }) {
  const icon =
    tool.status === 'running' ? (
      <Loader2 className="h-3 w-3 animate-spin" />
    ) : tool.status === 'ok' ? (
      <Check className="h-3 w-3" />
    ) : (
      <AlertTriangle className="h-3 w-3" />
    )
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs',
        tool.status === 'error' ? 'border-destructive/40 text-destructive' : 'border-border text-muted-foreground'
      )}
    >
      {icon}
      <span className="font-mono">{toolLabel(tool.name)}</span>
    </span>
  )
}

function Turn({ turn }: { turn: AgentTurn }) {
  if (turn.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground">{turn.text}</div>
      </div>
    )
  }

  const waiting = turn.status === 'streaming' && turn.text.length === 0 && turn.tools.length === 0

  return (
    <div className="space-y-2">
      {turn.tools.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {turn.tools.map((tool) => (
            <ToolChip key={tool.id} tool={tool} />
          ))}
        </div>
      ) : null}

      {waiting ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Thinking…
        </p>
      ) : null}

      {turn.text.length > 0 ? <AgentMarkdown text={turn.text} isStreaming={turn.status === 'streaming'} /> : null}

      {turn.error != null ? (
        <p className="flex items-start gap-2 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {turn.error}
        </p>
      ) : null}
    </div>
  )
}

export function AgentConsole({ traderId }: { traderId?: number }) {
  const { turns, isStreaming, send, stop, clear } = useAgentChat(traderId)
  const [draft, setDraft] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const transcriptEndRef = useRef<HTMLDivElement>(null)
  const consoleRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [turns])

  useEffect(() => {
    const node = consoleRef.current
    const parent = node?.parentElement
    if (node == null || parent == null) return
    const observer = new ResizeObserver(() =>
      parent.style.setProperty('--agent-console-height', `${node.getBoundingClientRect().height}px`)
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  function submit() {
    if (send(draft)) setDraft('')
  }

  return (
    <div
      ref={consoleRef}
      className={cn(
        'sticky bottom-0 z-30 mt-auto flex shrink-0 flex-col border-t border-border bg-background',
        isOpen && 'max-h-[50vh]'
      )}
    >
      <div className="flex shrink-0 items-center gap-2 px-4 py-1.5">
        <Sparkles className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Klear agent</span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
          POC
        </span>
        <div className="ml-auto flex items-center gap-1">
          {turns.length > 0 ? (
            <Button variant="ghost" size="icon" onClick={clear} aria-label="Clear conversation">
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen((open) => !open)}
            aria-label={isOpen ? 'Collapse agent' : 'Expand agent'}
          >
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {isOpen ? (
        <ScrollArea className="min-h-0 flex-1 border-t border-border">
          <div className="mx-auto min-h-[30vh] w-full max-w-3xl space-y-4 px-4 py-3">
            {turns.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Ask about anything you can see in the app — it reads and writes through your own permissions, and will
                ask before it changes anything.
              </p>
            ) : (
              turns.map((turn, index) => <Turn key={index} turn={turn} />)
            )}
            <div ref={transcriptEndRef} />
          </div>
        </ScrollArea>
      ) : null}

      <div className="flex shrink-0 justify-center border-t border-border px-4 py-2">
        <div className="flex w-full max-w-3xl items-end gap-2">
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onFocus={() => setIsOpen(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                submit()
              }
              if (event.key === 'Escape') setIsOpen(false)
            }}
            placeholder="Ask the Klear agent…"
            rows={1}
            className="max-h-32 min-h-[38px] resize-none border-0 shadow-none focus-visible:ring-0"
          />
          {isStreaming ? (
            <Button variant="outline" size="icon" onClick={stop} aria-label="Stop">
              <Square className="h-4 w-4" />
            </Button>
          ) : (
            <Button size="icon" onClick={submit} disabled={draft.trim().length === 0} aria-label="Send">
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
